import { inject, Injectable, Injector } from '@angular/core';
import { ConfirmationModalService } from '@core/customCDK/cdkCustomMdl/services/confirmation-modal.service';
import { TicketingRequestComponent } from '../ticketing-request/ticketing-request.component';
import { config } from '@core/interfaces/api_baseurl';
import { _RaiseTicket, RaiseGeneralTicket, RaiseTxnTicket } from './ticket.interface';
import { of, switchMap } from 'rxjs';
import { UserService } from '@core/auth/user/user.service';
import { User } from '@core/auth/user/user';
import { CategoryService } from '../category/helper/category.service';
import { ApiService } from '@core/auth/auth.service';

@Injectable({ providedIn: 'root' })
export class TicketingService {
  private confirmationModalService = inject(ConfirmationModalService);
  private _ApiService = inject(ApiService);
  private injector: Injector = inject(Injector);

  private userService!: UserService;
  public raiseTicketData: RaiseTxnTicket | null = null;

  constructor() { }

  /** Lazy initialization of UserService */
  private get user(): User {
    if (!this.userService) {
      this.userService = this.injector.get(UserService);
    }
    return this.userService.loginData.details;
  }

  /** Setter for raiseTicketData */
  set updateRaiseTicketData(data: Partial<RaiseTxnTicket> | null) {
    this.raiseTicketData = data ? { ...this.raiseTicketData, ...data } as RaiseTxnTicket : null;
  }

  /** Getter for raiseTicketData */
  getRaiseTicketData<K extends keyof RaiseTxnTicket>(key?: K): RaiseTxnTicket[K] | RaiseTxnTicket | null {
    return key ? this.raiseTicketData?.[key] ?? null : this.raiseTicketData;
  }

  /** Reset ticket system */
  resetTicketSystem(): void {
    this.raiseTicketData = null;
    this.injector.get(CategoryService).requestType = null;
  }

  /** Open support types modal */
  openSupportTypes(allowBack: boolean = true): void {
    this.updateRaiseTicketData = null;
    this.confirmationModalService.init(TicketingRequestComponent, {});
  }

  /** Fetch transaction services */
  getTxnServices() {
    return this._ApiService.post(config.ticketing.raise.get_services);
  }

  /** Raise a ticket */
  raiseTicket(data: RaiseGeneralTicket | RaiseTxnTicket) {
    if (this.raiseTicketData?.support_type_id == 2) { // 1: transaction, 2: General
      this.raiseTicketData.txn_issue_id = this.raiseTicketData.request_type_id as number;
      this.raiseTicketData.request_type_id = null;
    }

    // Clean up the incoming data by removing spaces from property names
    const cleanedData: any = {};
    Object.keys(data).forEach(key => {
      const cleanedKey = key.replace(/\s+/g, '');
      cleanedData[cleanedKey] = data[key as keyof (RaiseTxnTicket | RaiseGeneralTicket)];
    });

    const mergedData: _RaiseTicket & (RaiseGeneralTicket | RaiseTxnTicket) = {
      ...this.raiseTicketData,
      ...cleanedData,
      user_id: this.user.id,
      raised_by: this.user.id,
    };

    return this._ApiService.post(config.ticketing.raise.raise_ticket, mergedData).pipe(
      switchMap((res: any) => {
        res.email = mergedData.email ?? this.user.email;
        return of(res);
      })
    );
  }


}